import React, { useState } from 'react'

/* ─────────────────────────────────────────────────────────────
   Car Transfers — Service Request Form
───────────────────────────────────────────────────────────── */

const VEHICLE_OPTIONS = [
  { id: 'sedan',   label: 'Executive Sedan',    desc: 'Mercedes S-Class or equivalent'  },
  { id: 'suv',     label: 'Luxury SUV',          desc: 'Mercedes GLS or equivalent'      },
  { id: 'van',     label: 'Executive Van',        desc: 'Mercedes V-Class or equivalent'  },
  { id: 'maybach', label: 'Maybach / Ultra Luxury', desc: 'Subject to availability'      },
]

function ConfirmRow({ label, value }) {
  return (
    <div className="svc-confirm__row">
      <span className="svc-confirm__key">{label}</span>
      <span className="svc-confirm__val">{value}</span>
    </div>
  )
}

export default function CarTransfers({ onBack, member }) {
  const [submitted,   setSubmitted]   = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const [pickup,      setPickup]      = useState('')
  const [dropoff,     setDropoff]     = useState('')
  const [date,        setDate]        = useState('')
  const [time,        setTime]        = useState('')
  const [passengers,  setPassengers]  = useState(1)
  const [vehicle,     setVehicle]     = useState('sedan')
  const [specialReqs, setSpecialReqs] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    setShowConfirm(true)
  }

  const handleConfirm = () => {
    setShowConfirm(false)
    setSubmitted(true)
  }

  const selectedVehicle = VEHICLE_OPTIONS.find(v => v.id === vehicle)

  if (submitted) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted">
          <div className="svc-submitted__icon" aria-hidden="true">✦</div>
          <h2 className="svc-submitted__heading">Request Received</h2>
          <p className="svc-submitted__body">
            Your request has been received. We will be in touch within the hour.
          </p>
          <p className="svc-submitted__body svc-submitted__body--note">
            Once your booking is confirmed and payment received, you will receive
            an SMS confirmation with all transfer details.
          </p>
          <p className="svc-submitted__sig">— Mwuïlu</p>
          <button className="svc-btn-ghost" onClick={onBack}>Return to Dashboard</button>
        </div>
      </div>
    )
  }

  if (showConfirm) {
    return (
      <div className="db-page svc-page">
        <div className="svc-confirm">
          <p className="svc-confirm__overline">Please review your request</p>
          <h2 className="svc-confirm__heading">Car Transfer</h2>
          <div className="svc-confirm__rows">
            <ConfirmRow label="Pickup"      value={pickup    || '—'} />
            <ConfirmRow label="Destination" value={dropoff   || '—'} />
            <ConfirmRow label="Date"        value={date      || '—'} />
            <ConfirmRow label="Time"        value={time      || '—'} />
            <ConfirmRow label="Passengers"  value={`${passengers} passenger${passengers !== 1 ? 's' : ''}`} />
            <ConfirmRow label="Vehicle"     value={selectedVehicle?.label || '—'} />
            {specialReqs && <ConfirmRow label="Special requirements" value={specialReqs} />}
          </div>
          <div className="svc-confirm__actions">
            <button type="button" className="svc-btn-ghost"   onClick={() => setShowConfirm(false)}>Edit</button>
            <button type="button" className="svc-btn-primary" onClick={handleConfirm}>Confirm & Submit</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="db-page svc-page">

      <div className="svc-header">
        <button className="svc-back" onClick={onBack} aria-label="Back to dashboard">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </button>
        <div className="svc-header__title-wrap">
          <p className="svc-header__overline">Service Request</p>
          <h1 className="svc-header__title">Car Transfers</h1>
        </div>
      </div>

      <form className="svc-form" onSubmit={handleSubmit} noValidate>

        {/* ── Locations ── */}
        <div className="svc-section">
          <p className="svc-section__label">Journey</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="pickup">Pickup Location</label>
              <input
                id="pickup"
                type="text"
                className="svc-input"
                value={pickup}
                onChange={e => setPickup(e.target.value)}
                placeholder="Hotel, address or landmark"
                required
              />
            </div>
            <div className="svc-field">
              <label className="svc-label" htmlFor="dropoff">Destination</label>
              <input
                id="dropoff"
                type="text"
                className="svc-input"
                value={dropoff}
                onChange={e => setDropoff(e.target.value)}
                placeholder="Hotel, airport or address"
                required
              />
            </div>
          </div>
        </div>

        {/* ── Date & time ── */}
        <div className="svc-section">
          <p className="svc-section__label">Date &amp; Time</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="transferDate">Date</label>
              <input id="transferDate" type="date" className="svc-input" value={date} onChange={e => setDate(e.target.value)} required />
            </div>
            <div className="svc-field">
              <label className="svc-label" htmlFor="transferTime">Pickup Time</label>
              <input id="transferTime" type="time" className="svc-input" value={time} onChange={e => setTime(e.target.value)} required />
            </div>
          </div>
        </div>

        {/* ── Passengers ── */}
        <div className="svc-section">
          <p className="svc-section__label">Passengers</p>
          <div className="svc-steppers">
            <div className="svc-stepper">
              <span className="svc-stepper__label">Passengers</span>
              <div className="svc-stepper__controls">
                <button type="button" className="svc-stepper__btn" onClick={() => setPassengers(p => Math.max(1, p - 1))} disabled={passengers <= 1}>−</button>
                <span className="svc-stepper__value">{passengers}</span>
                <button type="button" className="svc-stepper__btn" onClick={() => setPassengers(p => Math.min(12, p + 1))}>+</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Vehicle ── */}
        <div className="svc-section">
          <p className="svc-section__label">Vehicle Preference</p>
          <div className="svc-vehicle-grid">
            {VEHICLE_OPTIONS.map(v => (
              <button
                key={v.id}
                type="button"
                className={`svc-vehicle-card${vehicle === v.id ? ' svc-vehicle-card--active' : ''}`}
                onClick={() => setVehicle(v.id)}
              >
                <span className="svc-vehicle-card__label">{v.label}</span>
                <span className="svc-vehicle-card__desc">{v.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Special requirements ── */}
        <div className="svc-section">
          <div className="svc-field">
            <label className="svc-label" htmlFor="transferSpecial">Special Requirements</label>
            <textarea
              id="transferSpecial"
              className="svc-textarea"
              rows={3}
              value={specialReqs}
              onChange={e => setSpecialReqs(e.target.value)}
              placeholder="Child seats, extra luggage, specific requirements…"
            />
          </div>
        </div>

        <div className="svc-form__footer">
          <button type="submit" className="svc-btn-primary svc-btn-primary--wide">
            Review Request
          </button>
        </div>

      </form>
    </div>
  )
}